import React, { useEffect, useState } from "react";
import { Form, Checkbox, Input, Radio, Spin } from "antd";
import { FormInstance } from "antd/es/form";
import { convertSDTM } from "../data-upload/utils";
import { updateTransferIdDetails } from "../../../services/update-transfer-id-details";
import { useOpenNotification } from "../../../components/Notification/notification";
import DeliverablesReview from "./deliverables-review";
import { validateDeliverables } from "./validator";
import { unmaskingComponentText, phdText } from "../../../components/Shared/utils";

interface UploadDTSProps {
  nextPage: any;
  uploadDTSFormValue: any;
  formRef: FormInstance<any>;
  transferId: string | undefined;
  handleDeliverablesDescription: (data: any) => void;
  handleInitialization: (value: boolean) => void;
  isInitialized: boolean;
  detailsSectionData?: {
    MSDCompound?: string;
    supplierOrganization?: string;
    supplierCompound?: string;
    DTSVersion?: string;
    transferId?: string;
    MSDCompoundType?: string;
    descriptionChange?: string;
    [key: string]: any;
  };
}

const UploadDTS: React.FC<UploadDTSProps> = ({
  nextPage,
  uploadDTSFormValue,
  formRef,
  transferId,
  handleDeliverablesDescription,
  handleInitialization,
  isInitialized,
  detailsSectionData,
}) => {
  const [names, setNames] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const { openNotification, contextHolder } = useOpenNotification();
  const formValues = Form.useWatch([], formRef);

  useEffect(() => {
    if (!isInitialized && uploadDTSFormValue) {
      const hasRadioValues =
        uploadDTSFormValue?.protectionHealthData?.value ||
        uploadDTSFormValue?.unblindingComponents?.value;

      formRef?.setFieldsValue({
        SDTMDeliverables: convertSDTM(uploadDTSFormValue.SDTMDeliverables),
        ...(hasRadioValues && {
          protectionHealthData: uploadDTSFormValue.protectionHealthData,
          unblindingComponents: uploadDTSFormValue.unblindingComponents,
        }),
      });

     handleInitialization(true);
    }
  }, [uploadDTSFormValue, formRef, isInitialized, handleInitialization]);

  useEffect(() => {
    if (transferId) {
      transferId !== detailsSectionData?.transferId &&
      formRef.setFieldsValue({
        SDTMDeliverables: undefined,
      });
      updateTransferIdDetails({ number: transferId.split("-")[0] })
        .then((result: any) => {
          setNames(result.data.deliverables.map((item: any) => item.fileName));
          handleDeliverablesDescription(result.data.deliverables);
          setLoading(false);
        })
        .catch(() => {
          openNotification("error", "failed to fetch deliverables");
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transferId, openNotification, formRef]);

  const handleInputChange = (
    type: "checkbox" | "radio",
    value: any,
    name: any,
  ) => {
    if (type === "checkbox") {
      const checked = value;
      const currentValues =
        formRef.getFieldValue(["SDTMDeliverables", name]) || {};

      formRef.setFieldsValue({
        SDTMDeliverables: {
          ...formRef.getFieldValue("SDTMDeliverables"),
          [name]: {
            ...currentValues,
            checked,
            comment: checked ? currentValues.comment : undefined, // Clear comment if unchecked
          },
        },
      });

      if (!checked) {
        // Explicitly clear the comment field when unchecked
        formRef.setFields([
          {
            name: ["SDTMDeliverables", name, "comment"],
            value: undefined,
          },
        ]);
      }
    } else if (type === "radio") {
      const currentValues = formRef.getFieldValue(name) || {};
      formRef.setFieldsValue({
        [name]: {
          ...currentValues,
          value,
        },
      });

      if (value !== "Yes") {
        // Clear the comment field when the radio value is not "Yes"
        formRef.setFields([
          {
            name: [name, "comment"],
            value: undefined,
          },
        ]);
      }
    }

    formRef.validateFields([name]);
  };

  const DisplayLoading = () => {
    if (!transferId) {
      return (
        <div style={{ marginBottom: "20px" }}>
          <span style={{ color: "#ff4d4f" }}>
            Please select a transfer ID to view the associated deliverables.
          </span>
        </div>
      );
    }

    if (!loading && names.length === 0) {
      return (
        <div style={{ marginBottom: "20px" }}>
          <span style={{ color: "red" }}>
            No deliverables attached to the selected transfer ID. Please select
            another transfer ID.
          </span>
        </div>
      );
    }

    return (
      <div style={{ marginBottom: "20px" }}>
        <Spin />
      </div>
    );
  };

  return (
    <div id="uploadDTSfile">
      {nextPage === "data-transfer" ? (
        <div id="uploadDTSContainer">
          <div style={{ fontSize: "20px", marginBottom: 10 }}>Deliverables</div>
          {names.length > 0 ? (
            <Form.Item label="">
              {names.map((name) => (
                <div
                  key={name}
                  style={{
                    marginBottom: 10,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                >
                  <Form.Item
                    name={["SDTMDeliverables", name, "checked"]}
                    valuePropName="checked"
                    noStyle
                  >
                    <Checkbox
                      onChange={(e) =>
                        handleInputChange("checkbox", e.target?.checked, name)
                      }
                    >
                      {name}
                    </Checkbox>
                  </Form.Item>
                  <Form.Item
                    shouldUpdate={(prev, curr) =>
                      prev.SDTMDeliverables?.[name]?.checked !==
                      curr.SDTMDeliverables?.[name]?.checked
                    }
                    noStyle
                  >
                    {({ getFieldValue }) =>
                      getFieldValue(["SDTMDeliverables", name, "checked"]) ? (
                        <Form.Item
                          name={["SDTMDeliverables", name, "comment"]}
                          validateTrigger={["onBlur", "onSubmit"]}
                          rules={[
                            {
                              whitespace: true,
                              message: "Empty comment is not allowed",
                            },
                          ]}
                          style={{ width: "55%", marginTop: 10, marginBottom: 10 }}
                        >
                          <Input
                            placeholder="Enter comment"
                            style={{ width: "100%" }}
                          />
                        </Form.Item>
                      ) : null
                    }
                  </Form.Item>
                </div>
              ))}
              <Form.Item
                name="SDTMDeliverables"
                noStyle
                rules={[
                  {
                    validator: (_, deliverables) =>
                      validateDeliverables(deliverables, false),
                  },
                ]}
              >
                <Input type="hidden" />
              </Form.Item>
            </Form.Item>
          ) : (
            <div>{DisplayLoading()}</div>
          )}
          <Form.Item
            label="Does the data contain any Protected Health Data?"
            name={["protectionHealthData", "value"]}
            tooltip={phdText()}
            rules={[
              {
                required: true,
                message: "This is a required field",
              },
            ]}
          >
            <Radio.Group
              onChange={(e) =>
                handleInputChange(
                  "radio",
                  e.target.value,
                  "protectionHealthData",
                )
              }
            >
              <Radio value="Yes">Yes</Radio>
              <Radio value="No">No</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            shouldUpdate={(prev, curr) =>
              prev?.protectionHealthData?.value !==
              curr?.protectionHealthData?.value
            }
            noStyle
          >
            {({ getFieldValue }) =>
              getFieldValue(["protectionHealthData", "value"]) === "Yes" ? (
                <Form.Item
                  name={["protectionHealthData", "comment"]}
                  style={{ marginTop: "-20px" }}
                  required={false}
                  label={
                    <span style={{ color: "grey" }}>
                      Specify how PHI data are masked, de-identified, or
                      redacted:
                    </span>
                  }
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: "This is required field",
                    },
                  ]}
                >
                  <Input placeholder="Enter comment" style={{ width: "55%" }} />
                </Form.Item>
              ) : null
            }
          </Form.Item>
          <Form.Item
            label="Does the data contain any unblinding/unmasking components?"
            name={["unblindingComponents", "value"]}
            tooltip={unmaskingComponentText()}
            rules={[
              {
                required: true,
                message: "This is a required field",
              },
            ]}
          >
            <Radio.Group
              onChange={(e) =>
                handleInputChange(
                  "radio",
                  e.target.value,
                  "unblindingComponents",
                )
              }
            >
              <Radio value="Yes">Yes</Radio>
              <Radio value="No">No</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            shouldUpdate={(prev, curr) =>
              prev?.unblindingComponents?.value !==
              curr?.unblindingComponents?.value
            }
            noStyle
          >
            {({ getFieldValue }) =>
              getFieldValue(["unblindingComponents", "value"]) === "Yes" ? (
                <Form.Item
                  name={["unblindingComponents", "comment"]}
                  style={{ marginTop: "-20px" }}
                  required={false}
                  label={<span style={{ color: "grey" }}>Specify:</span>}
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: "This is required field",
                    },
                  ]}
                >
                  <Input placeholder="Enter comment" style={{ width: "55%" }} />
                </Form.Item>
              ) : null
            }
          </Form.Item>
        </div>
      ) : (
        <DeliverablesReview initialDeliverables={formValues} />
      )}
      {contextHolder}
    </div>
  );
};

export default UploadDTS;
